import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { AdId, useAdConfigs, usePage, useWindow } from '../../contexts'
import { isGridVodPage, isVodPlaylist } from '../../types'
import { prettyLog } from '../../utils'
import { useGoogleAds } from '../../contexts/providers/GoogleAdsProvider/GoogleAdsContext'

export type SizeMapItem = {
  screen: [number, number]
  ad: [number, number]
}
export type SizeMap = SizeMapItem[]

export type IAd = {
  id?: AdId
  collapse?: boolean
  onRender?: (event: googletag.events.SlotRenderEndedEvent) => void
  sizeMap?: SizeMap
  mobileAdId?: AdId
  desktopAdId?: AdId
  sizeOverride?: googletag.GeneralSize | null
}
/**
 *  Component that allocates a Google Publisher Tag slot and tries to fill an ad.
 *  Width and height (or multiple sizes) have to be defined for the API to find a fitting ad.
 *  Targeting and other parameters are optional.
 *  More details at https://developers.google.com/publisher-tag/guides/get-started
 */
const Ad = ({
  id,
  mobileAdId,
  desktopAdId,
  collapse = false,
  sizeMap,
  onRender: renderCallback,
  sizeOverride,
}: IAd) => {
  const { isInitialized } = useGoogleAds()
  const adConfig = useAdConfigs({ id, mobileAdId, desktopAdId, renderCallback })
  const slotRef = useRef<googletag.Slot | null>(null)

  const destroySlotInternal = useCallback((elementId: string) => {
    if (!window.googletag?.pubads) {
      return
    }
    if (slotRef.current) {
      window.googletag.destroySlots([slotRef.current])
      slotRef.current = null
    } else {
      // Fallback: Find slot by element ID if ref is lost
      const slots = window.googletag.pubads().getSlots()
      const existing = slots.find(s => s.getSlotElementId() === elementId)
      if (existing) {
        window.googletag.destroySlots([existing])
      }
    }
    const el = document.getElementById(elementId)
    if (el) {
      el.innerHTML = ''
    }
  }, [])

  useEffect(() => {
    if (!adConfig || !window.googletag?.cmd || !isInitialized) {
      return
    }

    const { domId, name, size } = adConfig
    if (!domId) {
      return
    }
    let cancelled = false
    window.googletag.cmd.push(() => {
      if (cancelled) {
        return
      }
      try {
        if (slotRef.current) {
          return
        }
        destroySlotInternal(domId)

        // 2. Compute size logic
        const isPixelSize = size[0] === 1 && size[1] === 1
        const adSize: googletag.GeneralSize = sizeOverride
          ? (sizeOverride as googletag.GeneralSize)
          : isPixelSize
          ? (['fluid'] as googletag.GeneralSize)
          : (size as googletag.GeneralSize)

        // 3. Define Slot
        const newSlot = window.googletag.defineSlot(name, adSize, domId)
        if (!newSlot) {
          return
        }

        newSlot.addService(window.googletag.pubads())
        newSlot.setCollapseEmptyDiv(collapse)

        // 4. Handle Responsive Mapping
        if (sizeMap) {
          const mapping = window.googletag.sizeMapping()
          sizeMap.forEach(item => mapping.addSize(item.screen, item.ad))
          const builtMapping = mapping.addSize([0, 0], []).build()
          if (builtMapping) {
            newSlot.defineSizeMapping(builtMapping)
          }
        }

        slotRef.current = newSlot

        // 5. Display
        window.googletag.display(domId)
      } catch (e) {
        prettyLog('Ad: create/display error', { e }, 'error')
      }
    })

    return () => {
      cancelled = true
      slotRef.current = null
      // Destroy synchronously if possible, so the next mount's defineSlot
      // doesn't find a stale slot in the registry
      try {
        destroySlotInternal(domId)
      } catch (e) {
        prettyLog('Ad: destroy error', { e }, 'error')
      }
    }
  }, [
    adConfig,
    collapse,
    destroySlotInternal,
    sizeMap,
    sizeOverride,
    isInitialized,
  ])

  if (!adConfig) {
    return null
  }
  return <div id={adConfig.domId} />
}

const AdWithKey = ({
  id,
  mobileAdId,
  desktopAdId,
  collapse = false,
  sizeMap,
  onRender: renderCallback,
  sizeOverride,
}: IAd) => {
  const { asPath } = useRouter()
  const page = usePage()
  const win = useWindow()
  const { isInitialized } = useGoogleAds()

  const adsBlocked = useMemo(() => {
    return page?.PageMeta?.ads?.block.dfp
  }, [page?.PageMeta?.ads?.block.dfp])

  const key = useMemo(() => {
    const isPlaylistPage = page
      ? isGridVodPage(page) && isVodPlaylist(page.Content.PageGrid[0])
      : false
    return id + (isPlaylistPage ? '' : asPath)
  }, [asPath, id, page])

  if (!win || adsBlocked || !isInitialized) {
    return <div className={'ad-placeholder'} style={{ display: 'none' }} />
  }

  return (
    <Ad
      key={key}
      id={id}
      mobileAdId={mobileAdId}
      desktopAdId={desktopAdId}
      collapse={collapse}
      sizeMap={sizeMap}
      onRender={renderCallback}
      sizeOverride={sizeOverride}
    />
  )
}

export default AdWithKey as typeof Ad
